import { getSessionId } from '@aspendental/shared-utils/utils';

import { FormDataType } from '@/components/DynamicForm/types/DynamicFormTypes';

import {
	defaultTo,
	formatNumber,
	formatUSTel,
	formatDateUTC,
	is,
	join,
	path,
	safeParseFloat,
	selectPaths,
} from './utils';

// 1000 * 60 * 60 * 24 * 365.25
const YEAR_IN_MS = 31557600000;

export const diffInYears = (dateA: string, dateB: string) =>
	(new Date(dateA).getTime() - new Date(`${dateB}T00:00:00`).getTime()) / YEAR_IN_MS;

export const calcInchHt = (...args: (number | string)[]) =>
	((ft = 1, ih = 0) => ft * 12 + ih)(...args.map(safeParseFloat));

export const bmiCalc = (...args: (number | string)[]) =>
	((ih = 96, lb = 10) => (lb / ih ** 2) * 703)(...args.map(safeParseFloat));
// predicates
export const some = (...args: unknown[]) => args.flat().some(Boolean);
export const none = (...args: unknown[]) => !args.flat().some(Boolean);
export const all = (...args: unknown[]) => args.flat().every(Boolean);

type RequiredFnType = (value?: string | number | boolean) => boolean;
export const required: RequiredFnType = (value) => (Array.isArray(value) ? Boolean(value.length) : Boolean(value));

type EqFnType = (given: number, value?: number) => boolean;
export const eq: EqFnType = (given, value) => value === given;

type GtFnType = (given: number | string, value?: number | string) => boolean;
export const gt: GtFnType = (given, value) => (typeof value === 'number' || typeof value === 'string') && value > given;

type LtFnType = (given: number | string, value?: number | string) => boolean;
export const lt: LtFnType = (given, value) => (typeof value === 'number' || typeof value === 'string') && value < given;

type GteFnType = (given: number | string, value?: number | string) => boolean;
export const gte: GteFnType = (given, value) =>
	(typeof value === 'number' || typeof value === 'string') && value >= given;

type LteFnType = (given: number | string, value?: number | string) => boolean;
export const lte: LteFnType = (given, value) =>
	(typeof value === 'number' || typeof value === 'string') && value <= given;

type IncludesFnType = (given: string, value?: string) => boolean;
export const includes: IncludesFnType = (given, value) => {
	// whether value is a string
	if (is('String')(value)) {
		return (value as string).includes(given);
	}
	// the value must be a list for any checks to proceed
	if (Array.isArray(value)) {
		// whether both the value and the given are lists
		if (Array.isArray(given)) {
			// whether the value is a list, and both the given
			// and the head of the given are lists
			if (Array.isArray(given[0])) {
				return given[0].every((arg) => (value as unknown[]).includes(arg));
			}
			return given.every((arg) => (value as unknown[]).includes(arg));
		}
		// whether a scalar is in the given list
		return (value as unknown[]).includes(given);
	}
	return false;
};

// case insensitve version
export const includes_i: IncludesFnType = (given, value) =>
	is('String')(value) && (value as string).toLowerCase().includes(given.toLowerCase());

export const excludes: IncludesFnType = (given, value) => !includes(given, value);

export const excludes_i: IncludesFnType = (given, value) => !includes_i(given, value);

type OneOfFnType = (given: string[], value?: string) => boolean;
export const oneOf: OneOfFnType = (given, value) => typeof value === 'string' && given.includes(value);

type IntersectionFnType = (given: string[], value?: string[]) => boolean;
export const intersection: IntersectionFnType = (given, value) =>
	Array.isArray(value) ? given.filter((item) => value.includes(item)).length > 0 : false;
// start to deprecate this version of intersection to eventually allow for a piped version of this function
export const intersects = intersection;

type MinLengthFnType = (given: number, value?: string) => boolean;
export const minLength: MinLengthFnType = (given, value) => typeof value === 'string' && value.length >= given;

type MaxLengthFnType = (given: number, value?: string) => boolean;
export const maxLength: MaxLengthFnType = (given, value) => typeof value === 'string' && value.length <= given;

type IsIntegerFnType = (given: unknown) => boolean;
export const isInteger: IsIntegerFnType = (given) =>
	[
		[given].filter(is('String')).map((item) => Number.isInteger(parseFloat(item as string))),
		[given].filter(is('Number')).map(Number.isInteger),
		false,
	].flat()[0];

type ValidUSPhoneNumFnType = (given: string) => boolean;
export const validUSPhoneNum: ValidUSPhoneNumFnType = (given: string) => /\(\d{3}\) \d{3}-\d{4}/g.test(given);

type ImageSizeGTFnType = (given: number, value?: File | null) => boolean;
export const imageSizeGT: ImageSizeGTFnType = (given, file) => (file?.size ? file.size >= given : false);

type ImageSizeLTFnType = (given: number, value?: File | null) => boolean;
export const imageSizeLT: ImageSizeLTFnType = (given, file) => (file?.size ? file.size <= given : false);

export const always = (args: unknown, _: unknown): unknown => args;
export const concat = (args: unknown, value: unknown): unknown =>
	Array.isArray(value) ? (value as unknown[]).concat(args) : value;

export const coerceToBool = (val: unknown): boolean => !!val;

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
type PermittedFormDataFn = (...args: any[]) => unknown;

declare global {
	interface Window {
		Liine: {
			getSessionGUID: () => string;
		};
	}
}

export const permittedFormDataFns: Record<string, PermittedFormDataFn> = {
	/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
	default: (val: any): any => val,
	// data modifications
	increment: (val: string | number) => safeParseFloat(val) + 1,
	decrement: (val: string | number) => safeParseFloat(val) - 1,
	calcInchHt,
	bmiCalc,
	diffInYears,
	getSessionId: () => getSessionId(),
	getLiineGuid: () => globalThis.window?.Liine?.getSessionGUID() ?? '',
	// predicates
	some,
	none,
	all,
	required,
	eq,
	// use the inverted functions for this group bc the
	// default argument order expects the provided value to come last
	gt: lt,
	lt: gt,
	gte: lte,
	lte: gte,
	includes,
	includes_i,
	excludes,
	excludes_i,
	oneOf,
	intersection,
	intersects,
	minLength,
	maxLength,
	isInteger,
	validUSPhoneNum,
	// other useful utility functions
	always,
	concat,
	defaultTo,
	join,
	path,
	selectPaths,
};

export const inputTypeFormatters: Record<string, PermittedFormDataFn> = {
	checkbox: (_: string | undefined, __: FormDataType, checked: boolean) => checked,
	number: (value: string | undefined) => formatNumber(value),
	tel: (value: string | undefined) => formatUSTel(value),
	'date-text-input': (value: string | undefined) => formatDateUTC(value),
	'multi-select': (value: string, prevValue: string[] = []) => {
		return prevValue.includes(value) ? prevValue.filter((v) => v !== value) : [...prevValue, value];
	},
	image: (_: string | undefined, __: FormDataType, ___: boolean, files: File[] = []) => {
		const fileVal = files?.[0] ?? null;
		return fileVal;
	},
	default: (value: string | undefined) => (typeof value === 'undefined' ? '' : value),
};
